// P2b tanda 7b — alta de variaciones SIN atributos globales (P2-tax traerá los
// globales; cuando existan, el mismo botón ofrecerá elegirlos — registrado en
// el diseño). La PRIMERA variación de un producto pide además el EJE ("¿Por
// qué varía este producto?": Tono, Talla, Color u otro que escriba el usuario);
// las siguientes solo piden el valor. SKU sugerido con el sufijo del valor,
// precio con el default que pase la ficha (última fila, o el del simple al
// convertirlo), stock e imagen opcionales. El GUARDADO (padre primero, POST de
// la variación después, reversión del padre si falla) lo hace la ficha: este
// modal solo junta los datos y se los entrega a onCrear.
import React, { useState } from 'react';
import { ImagePlus } from 'lucide-react';
import Button from './ui/Button';
import FichaDialog from './ui/FichaDialog';
import ProductImageSelector from './ProductImageSelector';
import { generateVariationSku } from '../utils/variacionesEdit';
import { EJES_INICIALES } from '../utils/ejesVariacion';
import './AgregarVariacionModal.css';

export default function AgregarVariacionModal({
  abierto, onClose, onCrear,
  ejeExistente = null,          // nombre del atributo si el producto ya varía por algo
  ejesCanonicos = EJES_INICIALES, // lista canónica (config); crear ejes es SOLO en Configuración
  ejesMulti = null,             // tanda 8: nombres de los ejes si el producto varía por MÁS de uno
  combinacionExiste = null,     // tanda 8: (valores:{eje:val}) => bool — bloquea el duplicado
  skuPadre = '',                // base de la sugerencia de SKU
  precioPorDefecto = '',        // última fila, o el precio del simple al convertir
  stockPorDefecto = '',         // '' salvo la primera variación de un ex-simple
  esPrimera = false,
  nombreProducto = '',
  fotosDelProducto = null,      // P3-1 (punto 5): fotos ya subidas, para asociar por id
}) {
  // Tanda 8: producto de más de un eje → un campo por eje (P5), en vez del
  // único "valor". El resto del diálogo (SKU/precio/stock/imagen) no cambia.
  const esMulti = Array.isArray(ejesMulti) && ejesMulti.length > 1;
  const [valoresMulti, setValoresMulti] = useState(() => Object.fromEntries((ejesMulti || []).map((n) => [n, ''])));
  const [eje, setEje] = useState(ejeExistente || '');
  const [valor, setValor] = useState('');
  const [sku, setSku] = useState('');
  const [skuTocado, setSkuTocado] = useState(false);
  const [precio, setPrecio] = useState(precioPorDefecto);
  const [stock, setStock] = useState(stockPorDefecto);
  const [imagen, setImagen] = useState(null); // { id, src, alt } de la mediateca
  const [imgSel, setImgSel] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [errores, setErrores] = useState({});
  // Tanda 7c (punto 3): aviso del botón Generar cuando el padre no tiene SKU.
  const [avisoSku, setAvisoSku] = useState(false);

  if (!abierto) return null;

  const skuSugerido = skuTocado
    ? sku
    : (esMulti
      ? generateVariationSku(skuPadre, (ejesMulti || []).map((n) => ({ name: n, option: valoresMulti[n] || '' })))
      : generateVariationSku(skuPadre, valor));
  const pideEje = !esMulti && !ejeExistente;

  const crear = async () => {
    const e = {};
    if (esMulti) {
      (ejesMulti || []).forEach((n) => { if (!String(valoresMulti[n] || '').trim()) e[`eje_${n}`] = `Escribe el valor de ${n}.`; });
    } else {
      if (pideEje && !eje.trim()) e.eje = 'Elige por qué varía este producto.';
      if (!valor.trim()) e.valor = 'Escribe el valor de esta variación.';
    }
    // Tanda 8 (P5): una combinación que ya existe se rechaza — se edita la
    // existente, no se crea una duplicada.
    if (esMulti && !Object.keys(e).length && combinacionExiste
        && combinacionExiste(Object.fromEntries((ejesMulti || []).map((n) => [n, String(valoresMulti[n]).trim()])))) {
      e.combo = 'Esa combinación ya existe. Edítala en la tabla.';
    }
    setErrores(e);
    if (Object.keys(e).length) return;
    setGuardando(true); setError(null);
    try {
      await onCrear({
        eje: (ejeExistente || eje).trim(),
        option: valor.trim(),
        ejesValores: esMulti ? (ejesMulti || []).map((n) => ({ name: n, option: String(valoresMulti[n]).trim() })) : null,
        sku: skuSugerido.trim(),
        precio: String(precio || '').trim(),
        stock: String(stock || '').trim(),
        imageId: imagen ? imagen.id : null,
      });
      onClose();
    } catch (err) {
      setError(err.message); // el error real (Woo o la reversión) queda a la vista
      setGuardando(false);
    }
  };

  return (
    <>
      {/* Tanda 7c (punto 1): sobre el diálogo base de la ficha — scroll lock y
          estilo vienen de FichaDialog/LayerOverlay, no de este componente. */}
      <FichaDialog
        titulo={esPrimera ? 'Agregar la primera variación' : 'Agregar variación'}
        onClose={onClose}
        bloqueado={guardando}
        ancho={520}
        className="avm"
        footer={(
          <>
            <Button onClick={onClose} disabled={guardando}>Cancelar</Button>
            <Button variant="primary" onClick={crear} disabled={guardando}>
              {guardando ? 'Creando…' : 'Crear variación'}
            </Button>
          </>
        )}
      >
        {/* Tanda 8 (P5): producto de más de un eje → un campo POR EJE, todos
            obligatorios; el diálogo NO deja crear una combinación existente. */}
        {esMulti ? (
          <>
            <p className="avm-eje-fijo">Varía por: {ejesMulti.join(' y ')}</p>
            {ejesMulti.map((n) => (
              <label className="avm-field" key={n}>
                <span className="avm-label">{n} de la variación</span>
                <input className={`avm-input${errores[`eje_${n}`] ? ' avm-input--error' : ''}`} value={valoresMulti[n] || ''}
                  onChange={(e) => { const v = e.target.value; setValoresMulti((p) => ({ ...p, [n]: v })); setErrores((p) => ({ ...p, [`eje_${n}`]: undefined, combo: undefined })); }} />
                {errores[`eje_${n}`] && <span className="avm-error" role="alert">{errores[`eje_${n}`]}</span>}
              </label>
            ))}
            {errores.combo && <span className="avm-error" role="alert">{errores.combo}</span>}
          </>
        ) : (
          <>
            {/* Tanda 7c (punto 2): el eje se ELIGE de la lista canónica — sin texto
                libre; crear un eje nuevo se hace solo en Configuración. */}
            {pideEje ? (
              <div className="avm-field">
                <span className="avm-label">¿Por qué varía este producto?</span>
                <div className="avm-ejes">
                  {ejesCanonicos.map((s) => (
                    <button key={s} type="button" className={`avm-chip${eje === s ? ' avm-chip--on' : ''}`}
                      onClick={() => { setEje(s); setErrores((p) => ({ ...p, eje: undefined })); }}>{s}</button>
                  ))}
                </div>
                {errores.eje && <span className="avm-error" role="alert">{errores.eje}</span>}
                <span className="avm-help">¿Falta un eje? Se agrega en Configuración → Catálogo, y queda para toda la tienda.</span>
              </div>
            ) : (
              <p className="avm-eje-fijo">Varía por: {ejeExistente}</p>
            )}

            <label className="avm-field">
              <span className="avm-label">{ejeExistente ? `${ejeExistente} de la variación` : 'Valor'}</span>
              <input className={`avm-input${errores.valor ? ' avm-input--error' : ''}`} value={valor} autoFocus={!pideEje}
                placeholder="Ejemplo: Teacup, M, Rojo…"
                onChange={(e) => { setValor(e.target.value); setErrores((p) => ({ ...p, valor: undefined })); }} />
              {errores.valor && <span className="avm-error" role="alert">{errores.valor}</span>}
            </label>
          </>
        )}

        <div className="avm-row">
          <label className="avm-field avm-field--sku">
            <span className="avm-label">SKU (opcional)</span>
            {/* Tanda 7c (punto 3): "Generar" NUNCA se apaga en silencio — sin
                SKU de padre queda habilitado y al pulsarlo dice el motivo. */}
            <div className="avm-sku-row">
              <input className="avm-input" value={skuSugerido}
                onChange={(e) => { setSku(e.target.value); setSkuTocado(true); }} />
              <Button size="sm"
                title={String(skuPadre || '').trim()
                  ? 'Arma el SKU desde el SKU del producto y el valor'
                  : 'Primero pon el SKU del producto; el de cada variación se arma a partir de él.'}
                onClick={() => {
                  if (!String(skuPadre || '').trim()) { setAvisoSku(true); return; }
                  setSku(generateVariationSku(skuPadre, valor));
                  setSkuTocado(true);
                  setAvisoSku(false);
                }}>
                Generar
              </Button>
            </div>
            {avisoSku && (
              <span className="avm-error" role="alert">
                Primero pon el SKU del producto; el de cada variación se arma a partir de él.
              </span>
            )}
          </label>
          <label className="avm-field">
            <span className="avm-label">Precio</span>
            <input className="avm-input" type="number" min="0" value={precio} onChange={(e) => setPrecio(e.target.value)} />
          </label>
          <label className="avm-field">
            <span className="avm-label">Stock</span>
            <input className="avm-input" type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)} />
          </label>
        </div>

        <div className="avm-imagen">
          {imagen ? (
            <>
              <img src={imagen.src} alt="" />
              <Button size="sm" onClick={() => setImagen(null)}>Quitar la imagen</Button>
            </>
          ) : (
            <Button size="sm" icon={<ImagePlus size={14} />} onClick={() => setImgSel(true)}>Imagen (opcional)</Button>
          )}
        </div>

        {error && <p className="avm-error" role="alert">{error}</p>}
      </FichaDialog>

      <ProductImageSelector open={imgSel} onClose={() => setImgSel(false)}
        onSelect={(img) => { const i = Array.isArray(img) ? img[0] : img; if (i) setImagen(i); }}
        nombre={nombreProducto} variante={valor} fotosDelProducto={fotosDelProducto} />
    </>
  );
}
